import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared UI building blocks — one look across every page:
 *  - PageHeader: title + subtitle on the left, the "Signals" link on the RIGHT.
 *  - Tabs: the Signals-page segmented buttons (light bar, selected = black + white text).
 *  - DarkPanel: the Today's-AI-Signals dark card (slate gradient, white title).
 *  - dark table classes + SortTh (sortable column header), and standard buttons.
 */

export const btnPrimary =
  'inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition';
export const btnSecondary =
  'inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-200 disabled:opacity-50 transition';
/** Primary action inside a dark panel (black would disappear on the dark card). */
export const btnOnDark =
  'inline-flex items-center justify-center gap-1.5 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-200 disabled:opacity-50 transition';
export const linkText = 'font-semibold text-slate-900 underline-offset-2 hover:underline';
export const linkOnDark = 'font-semibold text-emerald-300 hover:text-emerald-200';
export const inputCls = 'fs-input';

/** Password field with an eye button that toggles showing what was typed.
 *  Takes the same props as a plain <input>. */
export function PasswordInput({ className = 'fs-input', ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input {...props} type={show ? 'text' : 'password'} className={`${className} pr-10`} />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 transition hover:text-slate-700"
        aria-label={show ? 'Hide password' : 'Show password'}
        aria-pressed={show}
      >
        {show ? (
          <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
            <path d="M14.12 14.12a3 3 0 11-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

/** Symbol search: pill-shaped field with a search icon, a clear (×) button and the
 *  submit button inside the pill. Submits on Enter or the button. Controlled —
 *  pass value/onChange/onSubmit; `busy` shows a spinner on the button. */
export function SearchBox({ value, onChange, onSubmit, placeholder = 'Search symbol', buttonLabel = 'Search', busy = false, allowEmpty = false, className = '' }) {
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit?.(value); }}
      className={`group flex items-center gap-2 rounded-2xl border border-slate-200 bg-white py-1.5 pl-3.5 pr-1.5 shadow-sm transition focus-within:border-emerald-400 focus-within:ring-4 focus-within:ring-emerald-500/10 ${className}`}
    >
      <svg className="h-[18px] w-[18px] shrink-0 text-slate-400 transition group-focus-within:text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        placeholder={placeholder}
        aria-label={placeholder}
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        className="min-w-0 flex-1 bg-transparent py-1 font-mono text-base font-bold tracking-wide text-slate-900 outline-none placeholder:font-sans placeholder:text-sm placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-400"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          aria-label="Clear"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>
      )}
      <button
        type="submit"
        disabled={busy || (!allowEmpty && !String(value || '').trim())}
        className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-label="Loading" />
        ) : buttonLabel}
      </button>
    </form>
  );
}

export function PageHeader({ title, subtitle, right, back = true }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {right}
        {back && (
          // Brand-gradient pill (same blue→violet as the logo) so the way back to
          // Signals stands out on every page — was a small grey chip that blended in.
          <Link
            to="/signals"
            className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-sky-500 to-indigo-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-md shadow-indigo-500/25 ring-1 ring-inset ring-white/20 transition hover:from-sky-400 hover:to-indigo-500 hover:shadow-lg active:scale-95"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <rect x="2" y="11" width="3" height="6" rx="1" opacity="0.6" />
              <rect x="7" y="8" width="3" height="9" rx="1" opacity="0.8" />
              <rect x="12" y="5" width="3" height="12" rx="1" />
              <circle cx="16.5" cy="3.5" r="2" />
            </svg>
            Signals
            <svg className="h-3.5 w-3.5 opacity-80" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.17 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
            </svg>
          </Link>
        )}
      </div>
    </div>
  );
}

/** Segmented buttons, same as the Signals page filter (All / Open / Stocks / ...).
 *  Wraps on small screens, so no button is ever cut off. */
export function Tabs({ tabs, active, onChange, className = '' }) {
  return (
    <div className={`flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1 ${className}`}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={`rounded-lg px-3 py-1.5 text-xs transition ${
            active === t.key ? 'bg-slate-900 font-semibold text-white' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Long tab sets (Market): ONE swipeable row of pills on phones — runs edge to edge so
 *  the cut-off pill at the screen edge shows there's more — and plain wrapped pills
 *  on wider screens. The selected pill scrolls itself into view. */
export function PillTabs({ tabs, active, onChange, className = '' }) {
  const refs = useRef({});
  useEffect(() => {
    refs.current[active]?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [active]);
  return (
    <div
      role="tablist"
      className={`scrollbar-none -mx-4 flex snap-x gap-2 overflow-x-auto px-4 py-0.5 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 ${className}`}
    >
      {tabs.map((t) => {
        const on = active === t.key;
        return (
          <button
            key={t.key}
            ref={(el) => { refs.current[t.key] = el; }}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(t.key)}
            className={`shrink-0 snap-start whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
              on
                ? 'bg-slate-900 text-white shadow-sm'
                : 'bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:text-slate-900 hover:ring-slate-300'
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

export function DarkPanel({ title, subtitle, right, children, bodyClassName = 'p-4', className = '' }) {
  return (
    <div className={`overflow-hidden rounded-2xl border border-slate-700/60 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-slate-200 shadow-md ${className}`}>
      {(title || right) && (
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3.5">
          <div className="min-w-0">
            {title && <p className="text-sm font-bold text-white">{title}</p>}
            {subtitle && <p className="text-[11px] text-slate-400">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}

/** Table classes for tables inside a DarkPanel (wrap the table in `dark.wrap` so a
 *  wide last column scrolls instead of being cut off on a phone). */
export const dark = {
  wrap: 'overflow-x-auto',
  table: 'w-full text-xs sm:text-sm',
  thead: 'bg-white/[0.03] text-[10px] uppercase tracking-wide text-slate-400',
  th: 'px-2.5 py-2 font-semibold sm:px-3',
  tr: 'border-t border-white/5 transition hover:bg-white/[0.03]',
  td: 'px-2.5 py-2 text-slate-200 sm:px-3',
  empty: 'px-3 py-8 text-center text-xs text-slate-500',
  up: 'text-emerald-400',
  down: 'text-rose-400',
};

/** Sortable column header: click toggles desc -> asc. */
export function SortTh({ label, sortKey, sort, onSort, align = 'right' }) {
  const on = sort?.key === sortKey;
  return (
    <th className={`${dark.th} text-${align}`}>
      <button
        type="button"
        onClick={() => onSort({ key: sortKey, dir: on && sort.dir === 'desc' ? 'asc' : 'desc' })}
        className={`inline-flex items-center gap-1 uppercase tracking-wide ${on ? 'text-white' : 'hover:text-slate-200'}`}
      >
        {label}
        <span className="text-[9px]">{on ? (sort.dir === 'desc' ? '▼' : '▲') : '↕'}</span>
      </button>
    </th>
  );
}

/** Sorts rows by a numeric field ("$1,234.5", "12.3%", "4.5M" all parse). */
export function sortRows(rows, sort, getValue) {
  if (!sort?.key) return rows;
  const num = (v) => {
    if (typeof v === 'number') return v;
    const s = String(v ?? '').replace(/[$,%\s]/g, '');
    const m = s.match(/^(-?\d+(?:\.\d+)?)([KMB])?$/i);
    if (!m) return Number.NEGATIVE_INFINITY;
    const mult = { K: 1e3, M: 1e6, B: 1e9 }[(m[2] || '').toUpperCase()] || 1;
    return parseFloat(m[1]) * mult;
  };
  const out = [...rows];
  out.sort((a, b) => {
    const d = num(getValue(a, sort.key)) - num(getValue(b, sort.key));
    return sort.dir === 'asc' ? d : -d;
  });
  return out;
}
